import type { ConversationId, MessageId } from '@cyphex/types';
import { createStore } from './createStore';

export interface LocalDraft {
  text: string;
  replyToId: MessageId | null;
  updatedAt: number;
}

interface DraftState {
  map: ReadonlyMap<string, LocalDraft>;
  version: number;
}

const STORAGE_KEY = 'cyphex.chat.drafts';
const EMPTY: LocalDraft = { text: '', replyToId: null, updatedAt: 0 };

function load(): Map<string, LocalDraft> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return new Map();
    return new Map(Object.entries(parsed as Record<string, LocalDraft>));
  } catch {
    return new Map();
  }
}

const store = createStore<DraftState>({ map: load(), version: 0 });

let persistTimer: ReturnType<typeof setTimeout> | null = null;

function persist(map: ReadonlyMap<string, LocalDraft>): void {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(map)));
    } catch {
      /* Storage full or blocked — drafts stay in memory for this session. */
    }
  }, 400);
}

export const draftStore = {
  subscribe: store.subscribe,

  select(conversationId: ConversationId): LocalDraft {
    return store.get().map.get(conversationId) ?? EMPTY;
  },

  set(conversationId: ConversationId, patch: Partial<LocalDraft>): void {
    const next = new Map(store.get().map);
    const current = next.get(conversationId) ?? EMPTY;
    const merged: LocalDraft = { ...current, ...patch, updatedAt: Date.now() };
    if (!merged.text && !merged.replyToId) next.delete(conversationId);
    else next.set(conversationId, merged);
    store.set((previous) => ({ map: next, version: previous.version + 1 }));
    persist(next);
  },

  clear(conversationId: ConversationId): void {
    draftStore.set(conversationId, { text: '', replyToId: null });
  },
};
